/**
 * Nav label resolution — TRUS-552.
 *
 * WHO OWNS A SIDEBAR LABEL
 * ------------------------
 * The server does. `GET /api/organizations/{id}/nav-config` already resolves
 * the whole precedence chain per language and hands back two fields per item:
 *
 *   label     = organization_nav_config.nav_label_override
 *               ?? page_capabilities.nav_label
 *               ?? page_capabilities.page_name
 *   label_es  = organization_nav_config.nav_label_override_es
 *               ?? page_capabilities.nav_label_es
 *
 * The sidebar used to render `t('nav.item.' + tabId, item.label)`, which
 * inverts that: when the static bundle has the key, i18next returns the
 * bundle value and the server's answer is never consulted. Two things broke
 * because of it, both silently:
 *
 *  1. **Per-org renames did not render.** `nav_label_override` /
 *     `nav_label_override_es` are writable from Navigation Layout Admin and
 *     are served correctly — the sidebar just threw them away for any tab id
 *     the bundle happened to know. Migration 476 renamed `care.referrals` to
 *     "Care Coordination" platform-wide in 2026; the sidebar has said
 *     "Referrals" ever since, because `en.json` has
 *     `nav.item.v2-referrals = "Referrals"`.
 *  2. **`nav_label_es` was dead weight.** 22 of 38 live nav items have no
 *     `nav.item.*` key in `es.json`, so a Spanish caseworker read the English
 *     `label` — for nine of them the Spanish string was sitting in the
 *     database the whole time.
 *
 * So: the server wins, and the bundle is what covers a language the database
 * has no column for. English never consults the bundle at all — `label` is
 * always populated, and preferring the bundle is exactly the bug above.
 *
 * Spanish is the one language with a first-class column, so it is the one
 * language checked by name here. Everything else falls through to the bundle,
 * which is per-language by construction.
 */

/** The `t()` shape TranslationContext exposes (key + string fallback). */
type TFunc = (key: string, fallback?: string) => string;

/**
 * A sentinel no bundle would ever contain, used to ask "does this key exist?"
 * without reaching for `i18n.exists` — the context hands components `t`, not
 * the i18next instance, and a missing key returns the fallback verbatim.
 */
const ABSENT = ' nav-label-absent';

function fromBundle(t: TFunc, key: string): string | null {
  let value: string;
  try {
    value = t(key, ABSENT);
  } catch {
    return null;
  }
  return value && value !== ABSENT ? value : null;
}

function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function isSpanish(language: string | null | undefined): boolean {
  return typeof language === 'string' && language.toLowerCase().startsWith('es');
}

function isEnglish(language: string | null | undefined): boolean {
  // An unset language is English: that is i18next's own default here, and it
  // keeps a sidebar rendering server labels rather than blanks while the
  // TranslationProvider is still initialising.
  return !language || language.toLowerCase().startsWith('en');
}

/**
 * Resolve one sidebar item's label for the active language.
 *
 * `bundleKey` is the `nav.item.<nav_tab_id>` key; `serverLabel` /
 * `serverLabelEs` are the item's `label` / `label_es` straight off nav-config.
 */
export function resolveNavLabel(
  {
    serverLabel,
    serverLabelEs,
    bundleKey,
    fallback,
  }: {
    serverLabel?: string | null;
    serverLabelEs?: string | null;
    bundleKey: string;
    /** Last resort when the server sent nothing at all — a page_key, usually. */
    fallback?: string | null;
  },
  language: string | null | undefined,
  t: TFunc,
): string {
  if (!isEnglish(language)) {
    if (isSpanish(language)) {
      const es = clean(serverLabelEs);
      if (es) return es;
    }
    const bundled = clean(fromBundle(t, bundleKey));
    if (bundled) return bundled;
  }

  return clean(serverLabel) ?? clean(fallback) ?? '';
}

/** Same rule for a section header (`nav.section.<section_key>`). */
export function resolveNavSectionLabel(
  {
    serverName,
    serverNameEs,
    sectionKey,
  }: {
    serverName?: string | null;
    serverNameEs?: string | null;
    sectionKey: string;
  },
  language: string | null | undefined,
  t: TFunc,
): string {
  return resolveNavLabel(
    {
      serverLabel: serverName,
      serverLabelEs: serverNameEs,
      bundleKey: `nav.section.${sectionKey}`,
      fallback: sectionKey,
    },
    language,
    t,
  );
}
